"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { resolveApiError } from "../../../helpers/reactHookForm/resolveApiError";

/**
 * @typedef {object} ApiErrorContextValue
 * @prop {string | null} message - The current error message.
 * @prop {(message: string | null) => void} setMessage - Function to set the error message.
 * @prop {{ [key: string]: string }} caughtErrors - The caught errors mapped by their keys.
 * @prop {(errors: { [key: string]: string }) => void} setCaughtErrors - Function to set the caught errors.
 * @prop {{ [key: string]: string }} uncaughtErrors - The uncaught errors mapped by their keys.
 * @prop {(uncaughtErrors: { [key: string]: string }) => void} setUncaughtErrors - Function to set the uncaught errors.
 * @prop {(params: { name: string }) => void} getApiError - Function to retrieve an API error by name.
 * @prop {(params: { parsedJson: any, form: import("react-hook-form").UseFormReturn<any> }) => void} resolveApiError - Function to resolve API errors using the response and form data.
 * @prop {() => void} resetApiErrors - Function to reset all API-related errors.
 */

/** @type {ApiErrorContextValue} */
export const API_ERROR_CONTEXT_DEFAULT_VALUES = {
  message: null,
  setMessage: () => {},
  caughtErrors: {},
  setCaughtErrors: () => {},
  uncaughtErrors: {},
  setUncaughtErrors: () => {},
  getApiError: () => {},
  resolveApiError: () => {},
  resetApiErrors: () => {},
};

export const ApiErrorContext = createContext(API_ERROR_CONTEXT_DEFAULT_VALUES);

/**
 * @param {object} param0 - Component props.
 * @param {boolean} [param0.isSearch] - Indicates if the search form is being used.
 * @param {React.ReactNode} [param0.children] - Optional child nodes to render within the component.
 */
export function ApiErrorProvider({ isSearch = false, children }) {
  const [message, setMessage] = useState(/** @type {string | null} */ (null));
  const [uncaughtErrors, setUncaughtErrors] = useState(/** @type {any} */ ({}));
  const [caughtErrors, setCaughtErrors] = useState(/** @type {any} */ ({}));

  const getApiError = useCallback(
    /**
     * @param {object} params
     * @param {string} params.name
     */
    ({ name }) => {
      return caughtErrors[name] ?? uncaughtErrors[name] ?? null;
    },
    [caughtErrors, uncaughtErrors],
  );

  const _resolveApiError = useCallback(
    /**
     * @param {object} args
     * @param {import("../../../types/alfred/ApiResponse").ApiResponse<any>} args.parsedJson
     * @param {import("react-hook-form").UseFormReturn<any>} args.form
     */
    ({ parsedJson, form }) => {
      resolveApiError({
        parsedJson,
        form,
        setMessage,
        setCaughtErrors,
        setUncaughtErrors,
        isSearch,
      });
    },
    [isSearch],
  );

  const resetApiErrors = useCallback(() => {
    setMessage(null);
    setCaughtErrors({});
    setUncaughtErrors({});
  }, []);

  /** @type {ApiErrorContextValue} */
  const contextValue = useMemo(() => {
    return {
      message,
      setMessage,
      uncaughtErrors,
      setUncaughtErrors,
      caughtErrors,
      setCaughtErrors,
      getApiError,
      resolveApiError: _resolveApiError,
      resetApiErrors,
    };
  }, [
    _resolveApiError,
    caughtErrors,
    getApiError,
    message,
    resetApiErrors,
    uncaughtErrors,
  ]);

  return (
    <ApiErrorContext.Provider value={contextValue}>
      {children}
    </ApiErrorContext.Provider>
  );
}

export function useApiError() {
  const value = useContext(ApiErrorContext);
  if (value === undefined) {
    throw new Error("useApiError must be used within an ApiErrorProvider");
  }
  return value;
}
